import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface CheckinRow {
  checkin_date: string;
  training_done: boolean;
  sleep_hours: number | null;
  pain_flags: Array<{ zone: string; severity?: string }> | null;
  nutrition_done: boolean;
  prevention_done: boolean;
}

interface FatigueResult {
  /** Computed fatigue score 0-10 */
  score: number;
  /** Human-readable breakdown */
  factors: string[];
  /** Number of check-ins used */
  checkinCount: number;
  /** Whether there's enough data to be meaningful */
  reliable: boolean;
}

const LOOKBACK_DAYS = 7;

/**
 * Computes a dynamic fatigue score (0-10) from recent check-ins.
 *
 * Factors considered:
 * - Consecutive training days (more = higher fatigue)
 * - Sleep deficit (< 7h average)
 * - Active pain flags
 * - Missed prevention / nutrition
 * - Training volume (ratio of training days in window)
 */
export function useDynamicFatigue(): {
  fatigue: FatigueResult | null;
  loading: boolean;
} {
  const { user } = useAuth();

  const { data: checkins, isLoading } = useQuery({
    queryKey: ["recent-checkins-fatigue", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const since = new Date();
      since.setDate(since.getDate() - LOOKBACK_DAYS);
      const sinceStr = since.toISOString().split("T")[0];

      const { data, error } = await supabase
        .from("program_checkins")
        .select("checkin_date, training_done, sleep_hours, pain_flags, nutrition_done, prevention_done")
        .eq("user_id", user.id)
        .gte("checkin_date", sinceStr)
        .order("checkin_date", { ascending: false })
        .limit(LOOKBACK_DAYS);

      if (error) {
        console.error("useDynamicFatigue: fetch error", error);
        return [];
      }
      return (data || []) as CheckinRow[];
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 min cache
    refetchOnWindowFocus: false,
  });

  const fatigue = useMemo<FatigueResult | null>(() => {
    if (!checkins || checkins.length === 0) return null;

    const factors: string[] = [];
    let score = 3; // baseline

    // 1. Training volume — ratio of training days
    const trainingDays = checkins.filter((c) => c.training_done).length;
    const trainingRatio = trainingDays / checkins.length;
    if (trainingRatio >= 0.85) {
      score += 2;
      factors.push(`${trainingDays}/${checkins.length} jours d'entraînement`);
    } else if (trainingRatio >= 0.7) {
      score += 1;
      factors.push(`Volume modéré (${trainingDays}/${checkins.length} jours)`);
    }

    // 2. Consecutive training days (most recent streak)
    let streak = 0;
    for (const c of checkins) {
      if (c.training_done) streak++;
      else break;
    }
    if (streak >= 5) {
      score += 2;
      factors.push(`${streak} jours consécutifs d'entraînement`);
    } else if (streak >= 3) {
      score += 1;
      factors.push(`${streak} jours consécutifs`);
    }

    // 3. Sleep deficit
    const sleepEntries = checkins.filter((c) => c.sleep_hours != null && c.sleep_hours > 0);
    if (sleepEntries.length >= 2) {
      const avgSleep = sleepEntries.reduce((s, c) => s + (c.sleep_hours || 0), 0) / sleepEntries.length;
      if (avgSleep < 6) {
        score += 2;
        factors.push(`Sommeil insuffisant (${avgSleep.toFixed(1)}h/nuit)`);
      } else if (avgSleep < 7) {
        score += 1;
        factors.push(`Sommeil limité (${avgSleep.toFixed(1)}h/nuit)`);
      } else if (avgSleep >= 8) {
        score -= 1;
        factors.push(`Bon sommeil (${avgSleep.toFixed(1)}h/nuit)`);
      }
    }

    // 4. Pain flags
    const recentPain = checkins
      .slice(0, 3) // last 3 days
      .filter((c) => {
        const flags = c.pain_flags as unknown as Array<{ zone: string }> | null;
        return flags && Array.isArray(flags) && flags.length > 0;
      });
    if (recentPain.length >= 2) {
      score += 2;
      factors.push("Douleurs signalées sur plusieurs jours");
    } else if (recentPain.length === 1) {
      score += 1;
      factors.push("Douleur signalée récemment");
    }

    // 5. Missed prevention (recovery neglect)
    const preventionDone = checkins.filter((c) => c.prevention_done).length;
    const preventionRatio = preventionDone / checkins.length;
    if (preventionRatio < 0.3 && trainingRatio > 0.5) {
      score += 1;
      factors.push("Peu de prévention malgré l'entraînement");
    }

    // Clamp 0-10
    score = Math.max(0, Math.min(10, score));

    return {
      score,
      factors,
      checkinCount: checkins.length,
      reliable: checkins.length >= 1,
    };
  }, [checkins]);

  return { fatigue, loading: isLoading };
}
